import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import { matterTypeProviderId } from "@smart/bridge-smokeball-basic";
import { Modal } from "@smart/itops-sb-design-system-dom";

import { EditOptions } from "./edit-options";
import { LanguageKey, baseLanguageKey } from "../../../../types";
import { GqlFieldValues, OnUpdateField } from "../../types";

type EditChoiceQuestionProps = {
  onUpdateField: OnUpdateField;
  field: GqlFieldValues;
  viewingLanguage: LanguageKey;
};

const getOptionsForLanguage = (
  baseOptions: GqlFieldValues["options"],
  translations: GqlFieldValues["translations"],
  viewingLanguage: LanguageKey,
) => {
  if (viewingLanguage === baseLanguageKey || !baseOptions?.length) {
    return baseOptions?.length ? baseOptions : [{ label: "", value: "" }];
  }

  const translatedOptions = translations?.find(
    (t) => t.language === viewingLanguage,
  )?.options;

  return baseOptions.map((baseOpt) => {
    const translated = translatedOptions?.find(
      (t) => t.value === baseOpt.value,
    );
    return {
      value: baseOpt.value,
      label: translated?.label || baseOpt.label,
    };
  });
};

export const EditChoice = ({
  field,
  onUpdateField,
  viewingLanguage,
}: EditChoiceQuestionProps) => {
  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;
  const {
    label,
    type,
    allowCustomResponse,
    options,
    supportsCustomValues,
    layout,
    field: mappedField,
    translations,
  } = field;

  const initialOptions = useMemo(
    () => getOptionsForLanguage(options, translations, viewingLanguage),
    [options, translations, viewingLanguage],
  );

  const formMethods = useForm<{
    allowCustomResponse?: boolean | null;
    options: { label: string; value: string }[];
  }>({
    defaultValues: {
      allowCustomResponse,
      options: initialOptions,
    },
  });
  const { control, handleSubmit, setValue } = formMethods;

  const submit = handleSubmit(
    async ({
      allowCustomResponse: updatedAllowCustomResponse,
      options: updatedOptions,
    }) => {
      await onUpdateField({
        field,
        updated: {
          options: updatedOptions.map((option) => ({
            label: option.label,
            value: option.value,
          })),
          allowCustomResponse: updatedAllowCustomResponse,
        },
      });
    },
  );

  const [optionsModal, showOptionsModal] = useState(false);
  const fieldOptions = useFieldArray({ control, name: "options" });

  useEffect(() => {
    const optionsForLanguage = getOptionsForLanguage(
      options,
      translations,
      viewingLanguage,
    );
    setValue("options", optionsForLanguage);
  }, [options, translations, viewingLanguage]);

  const [isShowingOtherOption, setIsShowingOtherOption] =
    useState(!!allowCustomResponse);

  const addOption = async (option: "text" | "other") => {
    if (option === "text") {
      fieldOptions.append({ label: "", value: "" });
    } else {
      setIsShowingOtherOption(true);
      setValue("allowCustomResponse", true);
    }
    await submit();
  };

  const removeOption = async (index?: number) => {
    if (index !== undefined && index !== null) {
      fieldOptions.remove(index);
    } else {
      setIsShowingOtherOption(false);
      setValue("allowCustomResponse", false);
    }
    await submit();
  };

  const isMappedField = field.layout && field.field;

  const updateOption = async (index: number, text: string | undefined) => {
    const currentValue = fieldOptions.fields[index].value;

    let valueToUpdate: string | undefined;
    if (isMappedField && field.field?.possibleValues?.includes(currentValue)) {
      valueToUpdate = currentValue || text || "";
    } else if (!isViewingBaseLanguage) {
      valueToUpdate = currentValue;
    } else {
      valueToUpdate = text || "";
    }

    fieldOptions.update(index, { label: text || "", value: valueToUpdate });
    await submit();
  };

  const moveOption = async (sourceIndex: number, destinationIndex: number) => {
    fieldOptions.move(sourceIndex, destinationIndex);
    await submit();
  };

  const isOptionsFixed =
    !!(
      layout?.providerId === matterTypeProviderId &&
      mappedField?.name &&
      ["matterType/name", "matterType/location"].includes(mappedField.name)
    ) || supportsCustomValues === false;

  const mappedFieldFixedOptionTooltip =
    isMappedField && isOptionsFixed
      ? "Additional options are not supported for this mapped field"
      : undefined;
  const tooltips = {
    addOptionButton: mappedFieldFixedOptionTooltip,
    addOtherButton: mappedFieldFixedOptionTooltip,
  };

  return (
    <>
      <EditOptions
        addOption={addOption}
        moveOption={moveOption}
        updateOption={updateOption}
        removeOption={removeOption}
        openOptionModal={() => showOptionsModal(true)}
        optionsFields={fieldOptions.fields}
        type={field.type as "choice" | "checkbox"}
        isShowingOtherOption={isShowingOtherOption}
        isOptionsFixed={isOptionsFixed}
        hasDynamicOptions={!!field.field?.dataSourceMapping}
        tooltips={tooltips}
        disabled={!isViewingBaseLanguage}
      />
      <Modal
        dataTestId="edit-options-modal"
        header={{
          icon: type === "choice" ? "solidListRadio" : "solidList",
          text: `Edit options for "${label}"`,
          iconBgColor: "bg-cyan-140",
        }}
        open={optionsModal}
        onClose={() => showOptionsModal(false)}
      >
        <EditOptions
          addOption={addOption}
          moveOption={moveOption}
          updateOption={updateOption}
          removeOption={removeOption}
          optionsFields={fieldOptions.fields}
          type={field.type as "choice" | "checkbox"}
          isShowingOtherOption={isShowingOtherOption}
          isOptionsFixed={isOptionsFixed}
          tooltips={tooltips}
          disabled={!isViewingBaseLanguage}
        />
      </Modal>
    </>
  );
};
